import { FC, PropsWithChildren, createContext, useContext, useEffect, useMemo, useState } from "react";
import { ApiServiceCollection, ApiServiceCollectionProps } from "./ApiServiceCollection.class";
import { AccessService } from "./access/AccessService.class";
import { AttributionService } from "./attribution/AttributionService.class";
import { BundleService } from "./bundle/BundleService.class";
import { OrganizationService } from "./organization/OrganizationService.class";
import { LinkService } from "./links/LinkService.class";
import { AuthContext } from "./Auth.context";
import { RequestManager } from "./RequestManager.class";
import { QuoteExperienceService } from "./quote-experience/QuoteExperienceService.class";
import { MockDatabaseCRUD } from "./mocks/MockDatabaseCRUD";


export const ApiServiceContext = createContext<{
    getApiServiceCollections: () => Record<string, ApiServiceCollection>;
    setMocked: (state: boolean) => void;
    mockCRUD: MockDatabaseCRUD | null;
    mocked: boolean;
}>({
    getApiServiceCollections: () => { throw new Error('ApiServiceContext is not within scope') },
    setMocked: (state: boolean) => { throw new Error('ApiServiceContext is not within scope') },
    mockCRUD: null,
    mocked: false
});

export type ApiServiceTestInjector = (apiServiceCollections: Record<string, ApiServiceCollection>) => void;

export const ApiServiceContextProvider: FC<PropsWithChildren<{
    mocked?: boolean;
    testInjector?: ApiServiceTestInjector;
}>> = ({ children, mocked, testInjector }) => {
    const [ _mocked, _setMocked ] = useState<boolean>(mocked === undefined ? true : mocked); // TODO - Set to false before deploying
    const mockCRUD = useMemo(() => new MockDatabaseCRUD(), []);
    const { authToken } = useContext(AuthContext);
    const requestManager = useMemo(() => new RequestManager(), []);

    const apiServiceCollections: Record<string, ApiServiceCollection> = useMemo(() => {
        if (!mockCRUD.seeded) {
            mockCRUD.seedDB();
        }
        const apiServiceCollectionProps: ApiServiceCollectionProps = {
            mocked: _mocked,
            mockCRUD: mockCRUD,
            requestManager
        };
        // Instantiate all services here
        const collections = {
            AccessService: new AccessService(apiServiceCollectionProps),
            AttributionService: new AttributionService(apiServiceCollectionProps),
            QuoteExperienceService: new QuoteExperienceService(apiServiceCollectionProps),
            BundleService: new BundleService(apiServiceCollectionProps),
            LinkService: new LinkService(apiServiceCollectionProps),
            OrganizationService: new OrganizationService(apiServiceCollectionProps),
        };
        if (testInjector) {
            testInjector(collections);
        }
        return collections;
    }, [ requestManager, _mocked, mockCRUD, testInjector ]);

    useEffect(() => {
        requestManager.setAuthToken(authToken);
    }, [requestManager, authToken]);

    const setMocked = (state: boolean) => _setMocked(state);
    const getApiServiceCollections = () => apiServiceCollections;

    return <ApiServiceContext.Provider value={{ getApiServiceCollections, setMocked, mockCRUD, mocked: _mocked }} >
        { children }
    </ApiServiceContext.Provider>
}